import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';

import Modal from '../../../components/modal/Modal';
import Button from '../../../components/button/Button';
import Input from '../../../components/form-item/Input';
import SvgLoadingAnimation from '../../../components/loading';
import DateDisplay, { DATE_DISPLAY_TYPE } from '../../../components/date-display/DateDisplay';
import {
  GitHubProjectRepository,
  GitHubTransfer,
  GitHubTreeEntry,
  useImportGitHubFileMutation,
  useLazyGetGitHubCommitsQuery,
  useLazyGetGitHubRawQuery,
  useLazyGetGitHubTransferQuery,
  useLazyGetGitHubTreeQuery,
} from '../../../app/services/github';
import {
  MediaItem,
  useCreateMediaItemMutation,
  useLazyGetMediaItemsQuery,
} from '../../../app/services/mediaItems';
import { useLazyGetShotsQuery } from '../../../app/services/shots';
import { variationNameForUpload } from '../../../utils/maSequenceFile';
import { parseRppProject } from '../ma/parseRppProject';
import { canonicalMediaKey } from '../ma/maTypes';
import {
  CrossShotMatch,
  buildShotItemIndex,
  collectRppMediaNames,
  findCrossShotMatches,
  findShotByFolderName,
  findShotFolderCandidates,
  isCommitNewer,
  isRppPath,
  matchRepoFiles,
  repoBaseName,
} from '../ma/repoShotSync';

/**
 * 「リポジトリから最新を取得」ウィザード（ShotEditModal から開く）。
 *
 * 流れ:
 * 1. リポジトリ選択（プロジェクトに複数リンクされている場合のみ）
 * 2. 自動解析: ショット名フォルダ探索 → RPP 全文取得・パース → RPP が参照する
 *    メディアをフォルダ内のファイルと照合 → 不足分は他ショットフォルダの
 *    Render / Mixdown を横断検索（他ショット参照の候補）→ ファイル別コミット
 *    日時と既存アイテムの更新日時を比較して「新規 / 更新 / 最新」に分類
 * 3. チェックリスト（新規・更新は既定で ON）
 * 4. 取得実行: import → 転送ポーリング → メディアアイテム登録
 *    - 参照先ショットに同名メディアあり → 参照行（他ショット参照）の作成のみ
 *    - 参照先ショットはあるがメディア未登録 → 参照先へ取り込んでから参照行を作成
 *    - 対応するショットが無い → 自ショットへ直接取り込み（参照不可・直接取得）
 *
 * 作成されたアイテムは silent（通知メール対象外）で登録し、onImported で
 * ShotEditModal に返して保存ボタン（更新して通知 / 通知せず更新）の決定に委ねる。
 */

// ===== 型 =====

type CandidateKind = 'rpp' | 'video' | 'audio';
type CandidateStatus = 'new' | 'updated' | 'uptodate';

type CandidateAction =
  | { type: 'direct' }
  /** 他ショットフォルダ産だが対応するショットが無い → 自ショットへ直接取得 */
  | { type: 'direct-fallback'; folderName: string }
  /** 参照先ショットに同名メディアあり → 参照行の作成のみ */
  | { type: 'reference'; sourceShotId: string; sourceShotName: string; sourceItemId: string }
  /** 参照先ショットはあるがメディア未登録 → 参照先へ取り込み後に参照行を作成 */
  | { type: 'import-and-reference'; sourceShotId: string; sourceShotName: string };

interface FetchCandidate {
  /** 一意 ID（リポジトリパス。参照のみ作成は `ref:` プレフィックス） */
  id: string;
  /** リポジトリ内パス（参照のみ作成は null = import 不要） */
  path: string | null;
  fileName: string;
  /** RPP 内の表記（表示用） */
  displayName: string;
  kind: CandidateKind;
  /** 最終コミット日時（取得できなかった場合 null） */
  commitDate: string | null;
  status: CandidateStatus;
  action: CandidateAction;
}

type RunState =
  | { step: 'pending' }
  | { step: 'importing' }
  | { step: 'registering' }
  | { step: 'done' }
  | { step: 'skipped' }
  | { step: 'failed'; message: string };

type Phase =
  | { step: 'repo' }
  | { step: 'scan'; message: string }
  | { step: 'folder'; candidates: string[]; allDirs: string[] }
  | { step: 'select'; rppUpToDate: boolean }
  | { step: 'run' }
  | { step: 'done' }
  | { step: 'error'; message: string };

export interface RepoFetchModalProps {
  projectId: string;
  shotId: string;
  shotName: string;
  /** ショットの現在のメディア（ShotEditModal の localItems） */
  mediaItems: MediaItem[];
  /** アクティブな MA シーケンスファイル（RPP の更新判定の基準） */
  activeMaFile: MediaItem | null;
  repositories: GitHubProjectRepository[];
  onClose: () => void;
  /** 取得実行で自ショットに作成されたメディアアイテム（silent 登録済み） */
  // eslint-disable-next-line no-unused-vars
  onImported: (items: MediaItem[]) => void;
}

// ===== 定数・ユーティリティ =====

/** コミット日時取得の同時リクエスト数 */
const COMMITS_CONCURRENCY = 4;
/** tree 全件取得の安全上限ページ数（500 件 × 20 = 1 万エントリー） */
const TREE_MAX_PAGES = 20;
/** 転送ポーリングの間隔と上限（動画の取り込みは数分かかりうる） */
const TRANSFER_POLL_INTERVAL_MS = 2000;
const TRANSFER_POLL_TIMEOUT_MS = 10 * 60 * 1000;

const KIND_ICONS: Record<CandidateKind, string> = {
  rpp: 'queue_music',
  video: 'movie',
  audio: 'music_note',
};

const STATUS_LABELS: Record<CandidateStatus, string> = {
  new: '新規',
  updated: '更新',
  uptodate: '最新',
};

/** RTK Query / Error 共通のエラーメッセージ抽出 */
const errorMessage = (err: unknown): string => {
  const e = err as { data?: { message?: string }; message?: string; error?: string };
  return e?.data?.message || e?.message || e?.error || '不明なエラーが発生しました';
};

// ===== Styled =====

const SelectableRow = styled.div<{ $selected: boolean; $disabled?: boolean }>`
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  border-radius: 4px;
  padding: 8px;
  background-color: ${({ $selected }) =>
    $selected ? 'var(--soremo-background)' : 'transparent'};

  @media (hover: hover) {
    &:hover {
      background-color: var(--soremo-background);
    }
  }
`;

const SelectIcon = styled.span.attrs({
  className: 'material-symbols-rounded',
})<{ $selected: boolean }>`
  font-size: 24px;
  flex-shrink: 0;
  color: ${({ $selected }) =>
    $selected ? 'var(--soremo-blue)' : 'var(--soremo-light-gray)'};
`;

const KindIcon = styled.span.attrs({
  className: 'material-symbols-rounded u-text-light-gray',
})`
  font-size: 18px;
  flex-shrink: 0;
`;

const StatusChip = styled.span<{ $muted?: boolean }>`
  flex-shrink: 0;
  font-size: 11px;
  line-height: 1;
  padding: 3px 6px;
  border-radius: 9999px;
  border: 1px solid
    ${({ $muted }) => ($muted ? 'var(--soremo-border)' : 'var(--soremo-blue)')};
  color: ${({ $muted }) => ($muted ? 'var(--soremo-light-gray)' : 'var(--soremo-blue)')};
`;

const EmptyState = styled.div.attrs({
  className: 'bodytext-13 u-text-light-gray',
})`
  text-align: center;
  padding: 24px 8px;
`;

const ScanBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 32px 8px;
`;

// ===== 本体 =====

const RepoFetchModal: React.FC<RepoFetchModalProps> = ({
  projectId,
  shotId,
  shotName,
  mediaItems,
  activeMaFile,
  repositories,
  onClose,
  onImported,
}) => {
  const [repositoryId, setRepositoryId] = useState<string | null>(
    repositories.length === 1 ? repositories[0].id : null,
  );
  const [phase, setPhase] = useState<Phase>(
    repositories.length === 1 ? { step: 'scan', message: '解析を開始しています…' } : { step: 'repo' },
  );
  const [folder, setFolder] = useState<string | null>(null);
  const [pendingFolder, setPendingFolder] = useState<string | null>(null);
  const [folderSearch, setFolderSearch] = useState('');
  const [candidates, setCandidates] = useState<FetchCandidate[]>([]);
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [runStates, setRunStates] = useState<Map<string, RunState>>(new Map());

  // アンマウント時に進行中の解析 / 取得を打ち切る
  const cancelledRef = useRef(false);
  useEffect(
    () => () => {
      cancelledRef.current = true;
    },
    [],
  );
  // 「中断」ボタン（現在のファイルの処理後に停止する）
  const abortRunRef = useRef(false);

  // ===== API hooks =====
  const [fetchTree] = useLazyGetGitHubTreeQuery();
  const [fetchCommits] = useLazyGetGitHubCommitsQuery();
  const [fetchRaw] = useLazyGetGitHubRawQuery();
  const [importFile] = useImportGitHubFileMutation();
  const [fetchTransfer] = useLazyGetGitHubTransferQuery();
  const [fetchShots] = useLazyGetShotsQuery();
  const [fetchMediaItems] = useLazyGetMediaItemsQuery();
  const [createMediaItem] = useCreateMediaItemMutation();

  // ===== 解析 =====

  const setScanMessage = useCallback((message: string) => {
    setPhase({ step: 'scan', message });
  }, []);

  /** tree をページングで全件取得する */
  const fetchFullTree = useCallback(
    async (repoId: string, path: string, recursive: boolean): Promise<GitHubTreeEntry[]> => {
      const entries: GitHubTreeEntry[] = [];
      let offset = 0;
      for (let page = 0; page < TREE_MAX_PAGES; page++) {
        const res = await fetchTree(
          { repositoryId: repoId, path, recursive, limit: 500, offset },
          false,
        ).unwrap();
        entries.push(...res.data.entries);
        const pagination = res.data.pagination;
        if (!pagination?.has_more || pagination.next_offset == null) break;
        offset = pagination.next_offset;
      }
      return entries;
    },
    [fetchTree],
  );

  /** フォルダ確定後の解析本体。候補リストを組み立てて select フェーズへ */
  const analyzeFolder = useCallback(
    async (repoId: string, targetFolder: string) => {
      setFolder(targetFolder);
      setScanMessage(`「${targetFolder}」のファイル一覧を取得しています…`);
      const files = await fetchFullTree(repoId, targetFolder, true);
      if (cancelledRef.current) return;

      // RPP: フォルダ内の最も浅い .rpp を採用
      const rppFiles = files
        .filter((f) => f.type === 'file' && isRppPath(f.path))
        .sort(
          (a, b) =>
            a.path.split('/').length - b.path.split('/').length ||
            a.path.localeCompare(b.path),
        );
      if (rppFiles.length === 0) {
        setPhase({
          step: 'error',
          message: `「${targetFolder}」内に RPP ファイルが見つかりませんでした`,
        });
        return;
      }
      const rppPath = rppFiles[0].path;

      setScanMessage('RPP の更新を確認しています…');
      let rppDate: string | null = null;
      try {
        const rppCommits = await fetchCommits(
          { repositoryId: repoId, path: rppPath, limit: 1 },
          false,
        ).unwrap();
        rppDate = rppCommits.data.commits[0]?.date ?? null;
      } catch {
        rppDate = null; // 日時不明は「更新あり」扱い（isCommitNewer）
      }
      if (cancelledRef.current) return;

      setScanMessage('RPP を解析しています…');
      const raw = await fetchRaw({ repositoryId: repoId, path: rppPath }, false).unwrap();
      if (cancelledRef.current) return;
      const parsed = parseRppProject(raw.data.content);
      const mediaNames = collectRppMediaNames(parsed);
      const { matches, missingNames } = matchRepoFiles(files, mediaNames);

      // 不足分は他ショットフォルダの Render / Mixdown を横断検索。
      // ショットフォルダが「カテゴリ/ショット名」のように入れ子の場合は
      // 同じカテゴリ（親フォルダ）内に限定する（巨大リポジトリの全件走査と
      // GitHub 側の tree 切り詰めを避ける）
      let crossMatches: CrossShotMatch[] = [];
      if (missingNames.size > 0) {
        setScanMessage('他ショットフォルダを探索しています…');
        const scopePrefix = targetFolder.includes('/')
          ? targetFolder.slice(0, targetFolder.lastIndexOf('/'))
          : '';
        const scopeFiles = await fetchFullTree(repoId, scopePrefix, true);
        if (cancelledRef.current) return;
        crossMatches = findCrossShotMatches(
          scopeFiles,
          targetFolder,
          missingNames,
          scopePrefix,
        );
      }

      // 自フォルダ内ファイルの最終コミット日時（更新判定用）
      setScanMessage('ファイルの更新日時を確認しています…');
      const commitDates = new Map<string, string | null>();
      const paths = matches.map((m) => m.path);
      for (let start = 0; start < paths.length; start += COMMITS_CONCURRENCY) {
        await Promise.all(
          paths.slice(start, start + COMMITS_CONCURRENCY).map(async (p) => {
            try {
              const res = await fetchCommits(
                { repositoryId: repoId, path: p, limit: 1 },
                false,
              ).unwrap();
              commitDates.set(p, res.data.commits[0]?.date ?? null);
            } catch {
              commitDates.set(p, null);
            }
          }),
        );
        if (cancelledRef.current) return;
      }

      // 他ショットフォルダ → プロジェクト内ショットのマッピング
      let projectShots: { id: string; shot_name: string }[] = [];
      if (crossMatches.length > 0) {
        setScanMessage('プロジェクトのショットを確認しています…');
        const shotsRes = await fetchShots(
          {
            project_id: projectId,
            filter: 'all',
            include_empty: true,
            limit: 500,
          },
          true,
        ).unwrap();
        projectShots = (shotsRes.data.shots ?? []).filter((s) => s.id !== shotId);
        if (cancelledRef.current) return;
      }

      const ownIndex = buildShotItemIndex(mediaItems);
      const list: FetchCandidate[] = [];

      // 1) RPP 本体（アクティブな MA シーケンスファイルと比較）
      const rppFileName = repoBaseName(rppPath);
      list.push({
        id: rppPath,
        path: rppPath,
        fileName: rppFileName,
        displayName: variationNameForUpload(rppFileName),
        kind: 'rpp',
        commitDate: rppDate,
        status: !activeMaFile
          ? 'new'
          : isCommitNewer(rppDate, activeMaFile)
            ? 'updated'
            : 'uptodate',
        action: { type: 'direct' },
      });

      // 2) 自フォルダ内で照合できたメディア
      for (const m of matches) {
        const existing = ownIndex.get(m.canonicalKey);
        const date = commitDates.get(m.path) ?? null;
        list.push({
          id: m.path,
          path: m.path,
          fileName: m.fileName,
          displayName: m.mediaName,
          kind: m.kind,
          commitDate: date,
          status: !existing ? 'new' : isCommitNewer(date, existing) ? 'updated' : 'uptodate',
          action: { type: 'direct' },
        });
      }

      // 3) 他ショットフォルダで見つかったメディア（参照 / 参照先へ取り込み / 直接取得）
      //    参照先ショットのメディア一覧はショット単位でキャッシュして使い回す
      const sourceItemsCache = new Map<string, MediaItem[]>();
      for (const cm of crossMatches) {
        const existing = ownIndex.get(cm.canonicalKey);
        // 既に自ショットにある（過去の取り込みや参照済み）は「最新」扱い。
        // 参照行は参照先の最新テイクへ動的解決されるため再取得は不要
        const status: CandidateStatus = existing ? 'uptodate' : 'new';
        const sourceShot = findShotByFolderName(projectShots, cm.folderName);
        if (!sourceShot) {
          list.push({
            id: cm.path,
            path: cm.path,
            fileName: cm.fileName,
            displayName: cm.mediaName,
            kind: cm.kind,
            commitDate: null,
            status,
            action: { type: 'direct-fallback', folderName: cm.folderName },
          });
          continue;
        }
        let sourceItems = sourceItemsCache.get(sourceShot.id);
        if (!sourceItems) {
          const res = await fetchMediaItems({ shot_id: sourceShot.id }, true).unwrap();
          sourceItems = res.data.media_items ?? [];
          sourceItemsCache.set(sourceShot.id, sourceItems);
          if (cancelledRef.current) return;
        }
        // 参照の参照（連鎖）は不可のため、参照行を除いて照合する
        const sourceIndex = buildShotItemIndex(
          sourceItems.filter((i) => !i.reference_source),
        );
        const sourceItem = sourceIndex.get(cm.canonicalKey);
        if (sourceItem) {
          list.push({
            id: `ref:${cm.canonicalKey}`,
            path: null,
            fileName: cm.fileName,
            displayName: cm.mediaName,
            kind: cm.kind,
            commitDate: null,
            status,
            action: {
              type: 'reference',
              sourceShotId: sourceShot.id,
              sourceShotName: sourceShot.shot_name,
              sourceItemId: sourceItem.id,
            },
          });
        } else {
          list.push({
            id: cm.path,
            path: cm.path,
            fileName: cm.fileName,
            displayName: cm.mediaName,
            kind: cm.kind,
            commitDate: null,
            status,
            action: {
              type: 'import-and-reference',
              sourceShotId: sourceShot.id,
              sourceShotName: sourceShot.shot_name,
            },
          });
        }
      }

      setCandidates(list);
      setCheckedIds(new Set(list.filter((c) => c.status !== 'uptodate').map((c) => c.id)));
      setRunStates(new Map());
      setPhase({ step: 'select', rppUpToDate: list[0].status === 'uptodate' });
    },
    [
      projectId,
      shotId,
      mediaItems,
      activeMaFile,
      fetchFullTree,
      fetchCommits,
      fetchRaw,
      fetchShots,
      fetchMediaItems,
      setScanMessage,
    ],
  );

  /**
   * リポジトリ確定 → ショットフォルダ探索（一意なら解析まで自動で進む）。
   * ルート直下に完全一致があれば即確定。見つからない場合は 2 階層目
   * （「カテゴリ/ショット名」構成のリポジトリ）まで探索する。
   */
  const startScan = useCallback(
    async (repoId: string) => {
      try {
        setScanMessage('ショットのフォルダを探しています…');
        const rootEntries = await fetchFullTree(repoId, '', false);
        if (cancelledRef.current) return;
        const rootDirs = rootEntries.filter((e) => e.type === 'dir');
        if (rootDirs.length === 0) {
          setPhase({
            step: 'error',
            message: 'リポジトリにフォルダが見つかりませんでした',
          });
          return;
        }

        const rootFound = findShotFolderCandidates(rootEntries, shotName);
        // ルート直下の完全一致のみ即確定（2 階層目により良い一致があり得るため）
        if (
          rootFound.length === 1 &&
          canonicalMediaKey(repoBaseName(rootFound[0])) === canonicalMediaKey(shotName.trim())
        ) {
          await analyzeFolder(repoId, rootFound[0]);
          return;
        }

        // 2 階層目（カテゴリ/ショット名）を探索
        setScanMessage('サブフォルダを探索しています…');
        const level2: GitHubTreeEntry[] = [];
        for (let start = 0; start < rootDirs.length; start += COMMITS_CONCURRENCY) {
          await Promise.all(
            rootDirs.slice(start, start + COMMITS_CONCURRENCY).map(async (dirEntry) => {
              try {
                const children = await fetchFullTree(repoId, dirEntry.path, false);
                level2.push(...children.filter((e) => e.type === 'dir'));
              } catch {
                // 読めないフォルダ（サブモジュール等）はスキップ
              }
            }),
          );
          if (cancelledRef.current) return;
        }

        const found = findShotFolderCandidates([...rootDirs, ...level2], shotName);
        if (found.length === 1) {
          await analyzeFolder(repoId, found[0]);
          return;
        }
        // 候補 0 件 or 複数 → 手動選択（候補を先頭に表示）
        const allDirs = [
          ...rootDirs.map((e) => e.path),
          ...level2.map((e) => e.path).sort((a, b) => a.localeCompare(b)),
        ];
        setPendingFolder(found[0] ?? null);
        setPhase({ step: 'folder', candidates: found, allDirs });
      } catch (err) {
        if (!cancelledRef.current) {
          setPhase({ step: 'error', message: errorMessage(err) });
        }
      }
    },
    [fetchFullTree, analyzeFolder, shotName, setScanMessage],
  );

  // リポジトリが 1 件なら自動開始
  const startedRef = useRef(false);
  useEffect(() => {
    if (startedRef.current || !repositoryId) return;
    startedRef.current = true;
    void startScan(repositoryId);
  }, [repositoryId, startScan]);

  const handleSelectRepository = useCallback(
    (repoId: string) => {
      setRepositoryId(repoId);
      startedRef.current = true;
      void startScan(repoId);
    },
    [startScan],
  );

  const handleConfirmFolder = useCallback(() => {
    if (!repositoryId || !pendingFolder) return;
    void (async () => {
      try {
        await analyzeFolder(repositoryId, pendingFolder);
      } catch (err) {
        if (!cancelledRef.current) {
          setPhase({ step: 'error', message: errorMessage(err) });
        }
      }
    })();
  }, [repositoryId, pendingFolder, analyzeFolder]);

  // ===== 取得実行 =====

  const setRunState = useCallback((id: string, state: RunState) => {
    setRunStates((prev) => {
      const next = new Map(prev);
      next.set(id, state);
      return next;
    });
  }, []);

  /** 転送（import）の完了をポーリングで待つ */
  const pollTransfer = useCallback(
    async (transferId: string): Promise<GitHubTransfer> => {
      const deadline = Date.now() + TRANSFER_POLL_TIMEOUT_MS;
      for (;;) {
        const res = await fetchTransfer(transferId, false).unwrap();
        const transfer = res.data.transfer;
        if (transfer.status === 'succeeded' || transfer.status === 'failed') {
          return transfer;
        }
        if (cancelledRef.current || Date.now() > deadline) {
          throw new Error('取り込みがタイムアウトしました');
        }
        await new Promise((resolve) => setTimeout(resolve, TRANSFER_POLL_INTERVAL_MS));
      }
    },
    [fetchTransfer],
  );

  const checkedCandidates = useMemo(
    () => candidates.filter((c) => checkedIds.has(c.id)),
    [candidates, checkedIds],
  );

  const handleRun = useCallback(async () => {
    if (!repositoryId || checkedCandidates.length === 0) return;
    abortRunRef.current = false;
    setPhase({ step: 'run' });
    setRunStates(new Map(checkedCandidates.map((c) => [c.id, { step: 'pending' as const }])));

    const imported: MediaItem[] = [];
    let failedCount = 0;
    for (const cand of checkedCandidates) {
      if (cancelledRef.current) return;
      if (abortRunRef.current) {
        setRunState(cand.id, { step: 'skipped' });
        continue;
      }
      try {
        if (cand.action.type === 'reference') {
          // 参照行の作成のみ（ファイルコピーなし）
          setRunState(cand.id, { step: 'registering' });
          const res = await createMediaItem({
            shot_id: shotId,
            reference: {
              shot_id: cand.action.sourceShotId,
              media_item_id: cand.action.sourceItemId,
            },
            variation_name: cand.displayName,
            silent: true,
          }).unwrap();
          imported.push(res.data.media_item);
        } else {
          setRunState(cand.id, { step: 'importing' });
          const importRes = await importFile({
            repositoryId,
            path: cand.path!,
          }).unwrap();
          const transfer = await pollTransfer(importRes.data.transfer.id);
          if (transfer.status !== 'succeeded' || !transfer.result?.file_key) {
            throw new Error(transfer.error || '取り込みに失敗しました');
          }
          setRunState(cand.id, { step: 'registering' });
          const fileKey = transfer.result.file_key;
          if (cand.action.type === 'import-and-reference') {
            // 参照先ショットへ取り込んでから、自ショットに参照行を作成
            const sourceRes = await createMediaItem({
              shot_id: cand.action.sourceShotId,
              file_key: fileKey,
              variation_name: variationNameForUpload(cand.fileName),
              silent: true,
            }).unwrap();
            const refRes = await createMediaItem({
              shot_id: shotId,
              reference: {
                shot_id: cand.action.sourceShotId,
                media_item_id: sourceRes.data.media_item.id,
              },
              silent: true,
            }).unwrap();
            imported.push(refRes.data.media_item);
          } else {
            const res = await createMediaItem({
              shot_id: shotId,
              file_key: fileKey,
              variation_name: variationNameForUpload(cand.fileName),
              silent: true,
            }).unwrap();
            imported.push(res.data.media_item);
          }
        }
        setRunState(cand.id, { step: 'done' });
      } catch (err) {
        failedCount += 1;
        setRunState(cand.id, { step: 'failed', message: errorMessage(err) });
      }
    }

    onImported(imported);
    if (imported.length > 0) {
      toast.success(`${imported.length} 件を取得しました`);
    }
    if (failedCount > 0) {
      toast.error(`${failedCount} 件の取得に失敗しました`);
    }
    setPhase({ step: 'done' });
  }, [
    repositoryId,
    shotId,
    checkedCandidates,
    createMediaItem,
    importFile,
    pollTransfer,
    setRunState,
    onImported,
  ]);

  // ===== チェックリスト操作 =====

  const toggleCandidate = useCallback((id: string) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const allChecked = candidates.length > 0 && checkedIds.size === candidates.length;
  const toggleAll = useCallback(() => {
    setCheckedIds(allChecked ? new Set() : new Set(candidates.map((c) => c.id)));
  }, [allChecked, candidates]);

  // ===== レンダリング =====

  /** 候補行のアクション説明（他ショット参照 / 直接取得） */
  const actionNote = (cand: FetchCandidate): React.ReactNode => {
    switch (cand.action.type) {
      case 'reference':
        return (
          <span className="bodytext-11 u-text-blue">
            他ショット参照: {cand.action.sourceShotName}
          </span>
        );
      case 'import-and-reference':
        return (
          <span className="bodytext-11 u-text-blue">
            他ショット参照: {cand.action.sourceShotName}（参照先へ取り込み）
          </span>
        );
      case 'direct-fallback':
        return (
          <span className="bodytext-11 u-text-light-gray">
            {cand.action.folderName} のファイル（参照不可・直接取得）
          </span>
        );
      default:
        return null;
    }
  };

  const renderRunLabel = (cand: FetchCandidate): React.ReactNode => {
    const state = runStates.get(cand.id) ?? { step: 'pending' };
    switch (state.step) {
      case 'pending':
        return <span className="bodytext-11 u-text-light-gray">待機中</span>;
      case 'importing':
        return <span className="bodytext-11 u-text-blue">取得中…</span>;
      case 'registering':
        return <span className="bodytext-11 u-text-blue">登録中…</span>;
      case 'done':
        return <span className="bodytext-11 u-text-blue">完了</span>;
      case 'skipped':
        return <span className="bodytext-11 u-text-light-gray">中断</span>;
      case 'failed':
        return (
          <span className="bodytext-11" style={{ color: 'var(--soremo-red, #d04438)' }}>
            失敗: {state.message}
          </span>
        );
    }
  };

  const renderCandidateRow = (
    cand: FetchCandidate,
    options: { selectable: boolean },
  ) => {
    const selected = checkedIds.has(cand.id);
    return (
      <SelectableRow
        key={cand.id}
        $selected={options.selectable && selected}
        $disabled={!options.selectable}
        onClick={options.selectable ? () => toggleCandidate(cand.id) : undefined}
      >
        <div className="u-row u-gap8 u-items-center">
          {options.selectable && (
            <SelectIcon $selected={selected}>
              {selected ? 'check_circle' : 'radio_button_unchecked'}
            </SelectIcon>
          )}
          <KindIcon>{KIND_ICONS[cand.kind]}</KindIcon>
          <div className="u-col u-fill" style={{ minWidth: 0 }}>
            <div className="u-row u-gap8 u-items-center">
              <span className="bodytext-13 u-text-ellipsis u-white-space" translate="no">
                {cand.displayName}
              </span>
              <StatusChip $muted={cand.status === 'uptodate'}>
                {STATUS_LABELS[cand.status]}
              </StatusChip>
              {cand.commitDate && (
                <span className="bodytext-11 u-text-light-gray" style={{ flexShrink: 0 }}>
                  <DateDisplay date={cand.commitDate} type={DATE_DISPLAY_TYPE.DATE_TIME} />
                </span>
              )}
            </div>
            <span className="bodytext-11 u-text-light-gray u-text-ellipsis u-white-space">
              {cand.path ?? cand.fileName}
            </span>
            {actionNote(cand)}
          </div>
          {!options.selectable && (
            <div style={{ flexShrink: 0 }}>{renderRunLabel(cand)}</div>
          )}
        </div>
      </SelectableRow>
    );
  };

  const renderBody = () => {
    switch (phase.step) {
      case 'repo':
        return (
          <div className="u-col u-gap8 u-w100">
            <p className="bodytext-13 u-text-light-gray">
              取得元のリポジトリを選択してください。
            </p>
            {repositories.map((repo) => (
              <SelectableRow
                key={repo.id}
                $selected={false}
                onClick={() => handleSelectRepository(repo.id)}
              >
                <div className="u-row u-gap8 u-items-center">
                  <KindIcon>folder_data</KindIcon>
                  <span className="bodytext-13 u-fill u-text-ellipsis u-white-space">
                    {repo.full_name}
                  </span>
                  <span className="bodytext-11 u-text-light-gray" style={{ flexShrink: 0 }}>
                    {repo.default_branch}
                  </span>
                </div>
              </SelectableRow>
            ))}
          </div>
        );

      case 'scan':
        return (
          <ScanBox>
            <SvgLoadingAnimation />
            <span className="bodytext-13 u-text-light-gray">{phase.message}</span>
          </ScanBox>
        );

      case 'folder': {
        const candidateSet = new Set(phase.candidates);
        const others = phase.allDirs.filter((d) => !candidateSet.has(d));
        const keyword = folderSearch.trim().toLowerCase();
        const visibleDirs = [...phase.candidates, ...others].filter(
          (dir) => !keyword || dir.toLowerCase().includes(keyword),
        );
        return (
          <div className="u-col u-gap8 u-w100">
            <p className="bodytext-13 u-text-light-gray">
              {phase.candidates.length > 0
                ? `ショット「${shotName}」に対応するフォルダの候補が複数見つかりました。解析するフォルダを選択してください。`
                : `ショット「${shotName}」に対応するフォルダを自動で特定できませんでした。解析するフォルダを選択してください。`}
            </p>
            <Input
              name="repo-fetch-folder-search"
              placeholder="フォルダ名で検索"
              value={folderSearch}
              onChange={(e) => setFolderSearch(e.target.value)}
            />
            {visibleDirs.length === 0 && (
              <EmptyState>該当するフォルダがありません</EmptyState>
            )}
            <div className="u-col u-w100">
              {visibleDirs.map((dir) => {
                const selected = pendingFolder === dir;
                return (
                  <SelectableRow
                    key={dir}
                    $selected={selected}
                    onClick={() => setPendingFolder(dir)}
                  >
                    <div className="u-row u-gap8 u-items-center">
                      <SelectIcon $selected={selected}>
                        {selected ? 'radio_button_checked' : 'radio_button_unchecked'}
                      </SelectIcon>
                      <KindIcon>folder</KindIcon>
                      <span className="bodytext-13 u-fill u-text-ellipsis u-white-space" translate="no">
                        {dir}
                      </span>
                      {candidateSet.has(dir) && (
                        <span className="bodytext-11 u-text-blue" style={{ flexShrink: 0 }}>
                          候補
                        </span>
                      )}
                    </div>
                  </SelectableRow>
                );
              })}
            </div>
          </div>
        );
      }

      case 'select':
        return (
          <div className="u-col u-gap8 u-w100">
            <p className="bodytext-13 u-text-light-gray">
              「{folder}」の RPP が参照するメディアと、ショットのメディアを比較しました。
              取得するファイルを選択してください。
              {phase.rppUpToDate && ' RPP はショットのシーケンスより新しくありません。'}
            </p>
            {candidates.length === 0 ? (
              <EmptyState>取得できるファイルがありません</EmptyState>
            ) : (
              <>
                <div className="u-row u-justify-end u-w100">
                  <Button
                    size="small"
                    theme="tertiary"
                    variant="text"
                    width="fit"
                    onClick={toggleAll}
                  >
                    {allChecked ? 'すべて解除' : 'すべて選択'}
                  </Button>
                </div>
                <div className="u-col u-w100">
                  {candidates.map((cand) => renderCandidateRow(cand, { selectable: true }))}
                </div>
              </>
            )}
          </div>
        );

      case 'run':
      case 'done':
        return (
          <div className="u-col u-gap8 u-w100">
            <p className="bodytext-13 u-text-light-gray">
              {phase.step === 'run'
                ? 'ファイルを取得しています。このまましばらくお待ちください…'
                : '取得が完了しました。'}
            </p>
            <div className="u-col u-w100">
              {checkedCandidates.map((cand) => renderCandidateRow(cand, { selectable: false }))}
            </div>
          </div>
        );

      case 'error':
        return <EmptyState>{phase.message}</EmptyState>;
    }
  };

  const isRunning = phase.step === 'run';

  return (
    <Modal
      onClose={isRunning ? () => undefined : onClose}
      title="リポジトリから最新を取得"
      footer={
        <>
          {isRunning ? (
            <Button
              size="small"
              theme="tertiary"
              variant="text"
              onClick={() => {
                abortRunRef.current = true;
              }}
            >
              中断
            </Button>
          ) : (
            <Button size="small" theme="tertiary" variant="text" onClick={onClose}>
              {phase.step === 'done' ? '閉じる' : 'キャンセル'}
            </Button>
          )}
          {phase.step === 'folder' && (
            <Button
              size="small"
              theme="primary"
              variant="filled"
              rightIcon="chevron_right"
              onClick={handleConfirmFolder}
              disabled={!pendingFolder}
            >
              このフォルダを解析
            </Button>
          )}
          {phase.step === 'select' && (
            <Button
              size="small"
              theme="primary"
              variant="filled"
              rightIcon="chevron_right"
              onClick={() => void handleRun()}
              disabled={checkedIds.size === 0}
            >
              {`${checkedIds.size} 件を取得`}
            </Button>
          )}
        </>
      }
    >
      {renderBody()}
    </Modal>
  );
};

export default RepoFetchModal;
